// frontend/src/telas/login/Login.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoEmbrapa from "../../assets/logo-embrapa.png";
import { IconButton, Input, InputAdornment, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  incrementLoad,
  decrementLoad,
  setUser,
  setToken,
  clearReducer,
  setToastType,
  setToastMessage,
} from "../../redux/reducer";
import {
  loginWithPin,
  fetchCurrentUser,
  sanitizeToken,
  setSessionToken,
} from "../../services/api/postgrest";
import {
  ButtonEntrar,
  ButtonCadastrar,
  DivCard,
  AvisoTermo,
  DivPagina,
  Formulario,
  ImagemIbi,
  SpanEntrar,
  ImageContainer,
  Texto,
  LinkTermoAviso,
  TextoLogin,
  TextoLoginContainer,
  TextoEmailSenha,
} from "./login-estilo";

import pdfAvisoDePrivacidade from "../../PdfDownloads/AvisoDePrivacidade.pdf";
import pdfTermoDeUso from "../../PdfDownloads/TermoDeUso.pdf";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const LOGIN_RESPONSE_MISSING_TOKEN_ERROR = "Resposta de login sem token";
const JWT_PART_SEPARATOR = ".";
const JWT_PAYLOAD_INDEX = 1;
const BASE64_URL_SAFE_MINUS_REGEX = /-/g;
const BASE64_URL_SAFE_UNDERSCORE_REGEX = /_/g;
const BASE64_WHITESPACE_REGEX = /\s/g;
const BASE64_STANDARD_PLUS = "+";
const BASE64_STANDARD_SLASH = "/";
const BASE64_PADDING_CHARACTER = "=";
const BASE64_PADDING_SEGMENT = 4;
const LOGIN_LOG_PREFIX = "[Login]";
const TOKEN_LOG_PREFIX = "[Login][Token]";
const USER_PROFILE_LOG_PREFIX = "[Login][Perfil]";
const PIN_REQUIRED_LENGTH = 6;

const sanitizeBase64Segment = (segment) => {
  const trimmedSegment = segment.replace(BASE64_WHITESPACE_REGEX, "");
  const base64Segment = trimmedSegment
    .replace(BASE64_URL_SAFE_MINUS_REGEX, BASE64_STANDARD_PLUS)
    .replace(BASE64_URL_SAFE_UNDERSCORE_REGEX, BASE64_STANDARD_SLASH);
  const paddingSize =
    (BASE64_PADDING_SEGMENT - (base64Segment.length % BASE64_PADDING_SEGMENT)) %
    BASE64_PADDING_SEGMENT;

  return base64Segment + BASE64_PADDING_CHARACTER.repeat(paddingSize);
};

const decodeTokenPayload = (token) => {
  try {
    if (typeof token !== "string") {
      return {};
    }

    const tokenSegments = token.split(JWT_PART_SEPARATOR);

    if (tokenSegments.length <= JWT_PAYLOAD_INDEX) {
      return {};
    }

    const sanitizedPayload = sanitizeBase64Segment(
      tokenSegments[JWT_PAYLOAD_INDEX]
    );

    return JSON.parse(atob(sanitizedPayload));
  } catch (error) {
    console.warn(`${TOKEN_LOG_PREFIX} Não foi possível decodificar o token`, error);
    return {};
  }
};

const Login = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [larguraTela, setLarguraTela] = useState(window.innerWidth);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  useEffect(() => {
    dispatch(clearReducer());
  }, []);

  const showError = (e) => {
    const statusCode = e?.response?.status;
    console.error(`${LOGIN_LOG_PREFIX} Erro durante tentativa de login`, {
      status: statusCode,
      message: e?.message,
      data: e?.response?.data,
    });

    switch (e?.response?.status) {
      case 400:
        dispatch(setToastType("warning"));
        dispatch(setToastMessage("Dados Inválidos!"));
        break;
      case 401:
        dispatch(setToastType("warning"));
        dispatch(setToastMessage("Sem autorização!"));
        break;
      default:
        dispatch(setToastType("error"));
        dispatch(setToastMessage("Erro Interno!"));
    }
  };

  const handlePinChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, PIN_REQUIRED_LENGTH);
    setPin(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email || pin.length !== PIN_REQUIRED_LENGTH) {
      dispatch(setToastType("warning"));
      dispatch(setToastMessage("Informe e-mail e PIN com 6 dígitos."));
      return;
    }

    console.info(`${LOGIN_LOG_PREFIX} Iniciando tentativa de login`, {
      email,
      pinLength: pin.length,
    });

    dispatch(incrementLoad());

    try {
      const { data } = await loginWithPin({
        email,
        pin,
      });

      console.info(`${LOGIN_LOG_PREFIX} Resposta recebida do endpoint /rpc/login`, {
        temRespostaArray: Array.isArray(data),
        tamanhoResposta: Array.isArray(data) ? data.length : undefined,
      });

      const token = Array.isArray(data) ? data[0]?.token : data?.token;
      const sanitizedToken = sanitizeToken(token);

      if (!sanitizedToken) {
        throw new Error(LOGIN_RESPONSE_MISSING_TOKEN_ERROR);
      }

      console.info(`${TOKEN_LOG_PREFIX} Token recebido`, {
        tamanhoToken: sanitizedToken.length,
      });

      setSessionToken(sanitizedToken);

      let usuario = decodeTokenPayload(sanitizedToken);

      try {
        const { data: perfil } = await fetchCurrentUser(sanitizedToken);
        if (Array.isArray(perfil) && perfil.length > 0) {
          usuario = { ...perfil[0], ...usuario };
          console.info(`${USER_PROFILE_LOG_PREFIX} Perfil detalhado carregado`, {
            registros: perfil.length,
          });
        }
      } catch (perfilErro) {
        console.warn(`${USER_PROFILE_LOG_PREFIX} Falha ao carregar dados detalhados do usuário`, perfilErro);
      }

      dispatch(setUser(usuario));
      dispatch(setToken(sanitizedToken));
      console.info(`${LOGIN_LOG_PREFIX} Login concluído com sucesso, redirecionando para /inicio`);
      navigate("/inicio");
    } catch (e) {
      showError(e);
      console.error(`${LOGIN_LOG_PREFIX} Exceção capturada durante o login`, e);
    } finally {
      dispatch(decrementLoad());
    }
  };
  useEffect(() => {
    console.debug(`${LOGIN_LOG_PREFIX} Largura da tela atualizada`, { larguraTela });
  }, [larguraTela]);

  const textoLoginEmailSenha = "(e-mail e PIN de 6 dígitos)";
  return (
    <DivPagina>
      {larguraTela > 700 && (
        <ImageContainer>
          <Texto>Sistema de Apoio</Texto>
          <Texto>à Caracterização de</Texto>
          <Texto>Imóveis Rurais</Texto>
        </ImageContainer>
      )}
      <DivCard style={{ width: larguraTela > 700 ? "40%" : "100%" }}>
        {larguraTela < 700 && (
          <div style={{ marginBottom: 30, textAlign: "center" }}>
            <h4>Sistema de Apoio</h4>
            <h4>à Caracterização de</h4>
            <h4>Imóveis Rurais</h4>
          </div>
        )}
        <AvisoTermo>
          <LinkTermoAviso
            href={pdfAvisoDePrivacidade}
            download={"AvisoDePrivacidade.pdf"}
          >
            Aviso de Privacidade
          </LinkTermoAviso>
          <LinkTermoAviso href={pdfTermoDeUso} download={"TermoDeUso.pdf"}>
            Termo de uso
          </LinkTermoAviso>
        </AvisoTermo>

        <Formulario>
          <TextoLoginContainer>
            <TextoLogin>Login</TextoLogin>
            <TextoEmailSenha>{textoLoginEmailSenha}</TextoEmailSenha>
          </TextoLoginContainer>

          <TextField
            placeholder="E-mail"
            variant="outlined"
            type="email"
            color="info"
            id="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            InputProps={{
              style: {
                borderRadius: "50px",
                height: 40,
              },
            }}
            style={{ width: larguraTela > 700 ? "70%" : "90%" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit(e);
              }
            }}
          />

          <Input
            disableUnderline
            variant="outlined"
            placeholder="PIN (6 dígitos)"
            color="info"
            style={{
              borderRadius: "50px",
              height: 40,
              alignItems: "center",
              border: "1px solid #00000055",
              width: larguraTela > 700 ? "70%" : "90%",
              paddingLeft: 10,
              paddingRight: 20,
            }}
            id="pin"
            value={pin}
            onChange={handlePinChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit(e);
              }
            }}
            type={showPassword ? "text" : "password"}
            inputProps={{
              inputMode: "numeric",
              pattern: "\\d{6}",
              maxLength: 6,
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />

          <ButtonEntrar
            style={{ width: larguraTela > 700 ? "70%" : "90%" }}
            onClick={handleSubmit}
          >
            <SpanEntrar>Entrar</SpanEntrar>
          </ButtonEntrar>
          <ButtonCadastrar
            style={{ width: larguraTela > 700 ? "70%" : "90%" }}
            onClick={() => navigate("/cadastro")}
          >
            <SpanEntrar>Cadastrar</SpanEntrar>
          </ButtonCadastrar>
        </Formulario>

        <ImagemIbi src={LogoEmbrapa} alt="Minha Imagem" />
      </DivCard>
    </DivPagina>
  );
};

export default Login;
